/**
 * LightingModule — the key light, the ambient and the exposure CURVE
 * (Graphics G3, Phase B; physical rework 2026-09-25, realism §8 R1–R3).
 *
 * What this module owns:
 *   - The sun `DirectionalLight`. Its path is the latitude/season solar path
 *     (`lib/lighting` — latitude from the player's absolute Z, the season from
 *     the world day); its COLOUR and INTENSITY are the atmosphere's own
 *     transmittance toward the sun × E0 × the weather's deck transmittance
 *     (`lib/photometry.lightModel`), faded to 0 between +1° and −1°. Golden
 *     hour, red sunsets, a dark night and a flat grey storm fall out of that;
 *     the four authored bands and the WebGPU "bleached noon" trim are gone.
 *   - The `HemisphereLight`: tinted from the Hillaire sky itself
 *     (`lib/sky/sky_sampler`); where the sky PMREM (`scene.environment`)
 *     already carries the sky's diffuse it only keeps a small floor plus the
 *     night floor, so the two no longer double-count.
 *   - Tone mapping + exposure. Every owned light is written PRE-EXPOSED
 *     (lux × `luxToScene` from the curve), so the renderer's
 *     `toneMappingExposure` stays at EXPOSURE_REF × the metered correction
 *     (PostModule's clamped auto exposure) × the player's compensation. The
 *     one writer of `toneMappingExposure` on the ground is THIS module.
 *   - CSM sun shadows (node CSM), `autoUpdate` gated (re-render only when the
 *     sun moved ≥ 0.18° or the casters changed; never while the key is out).
 *   - `sky_lighting_profile` rows: a TINT OVERRIDE only — a row equal to its
 *     seed value changes nothing; a live-ops row nudges the colour and scales
 *     the intensity by its ratio to the seed.
 *
 * IBL (scene.environment) is the engine's PMREM bake in SkyModule.
 * Levers: `__lighting()`, `__exposure()`, `__sunSeason(doy | null, lat?)`.
 */
import * as THREE from 'three/webgpu';
import { CSMShadowNode } from 'three/addons/csm/CSMShadowNode.js';
import type { App, IModule } from 'enginex';
import { backportCsmShadowNode } from 'enginex/render/backports/threeR186.js';
import { atlas } from '../state.js';
import { CLOUD_HIGH_KM } from '../lib/sky/cloud_light.js';
import { worldEnv, TINT_BLEND } from '../lib/world_env.js';
import { bus } from '../bus.js';
import type { AssetModule } from './AssetModule.js';
import { type Band, bandForTimeOfDay, sunPosition, sunDirection, latitudeForZ, worldDayOfYear, setSunObserver, sunObserver, pinSunSeason, noonElevationDeg } from '../lib/lighting.js';
import { activePreset } from '../lib/quality_tiers.js';
import { Phase, Sub, makeSystem } from '../engine-compat.js';
import { currentTod, uNightFactor } from './SkyModule.js';
import { skySampler, nightSkyLive, nightAir } from '../lib/sky/sky_sampler.js';
import { skyglowLive } from '../lib/light_pollution_live.js';
import { interiorHandles } from '../lib/stage3_fx.js';
import {
  lightModel, exposureState, xrNightLiftEv, lightFrame, luxToScene as luxToSceneFor, emissiveEvScale, emissiveNits, keyLuminanceNits, EMISSIVE_NITS,
  E0_LUX, EXPOSURE_REF, GROUND_ALBEDO, EV_DAY, lum709, NIGHT_FLOOR_LUX,
  moonModel, nightFloorLux, cloudiness as cloudinessOf, tCloud as tCloudOf, moonIllumFraction, MOON_SYNODIC_DAYS, type MoonModel,
} from '../lib/photometry.js';
import { nightIblApplied } from '../lib/sky/night_ibl.js';
import { moonAt, pinMoonPhase, moonPhasePinned } from '../lib/night_sky_schedule.js';
import { cloudKey } from '../lib/sky/cloud_light.js'; // sky/post (2026-09-27): the clouds' moon key lever
import { applyCaveHookToHemisphere, applyCaveHookToEnv } from '../lib/cave_light_hook.js'; // shader gaps (2026-09-29): the cave hook

interface Profile { envmapAssetId: bigint; sunColorHex: string; sunIntensity: number; ambientIntensity: number; }

/** The seeded band rows (tools/asset-pipeline/seed.mjs): a row equal to these is NEUTRAL under the physical model. */
const SEED_PROFILE: Record<Band, { hex: string; sun: number; amb: number }> = {
  dawn: { hex: '#ffd9a0', sun: 1.4, amb: 0.35 },
  day: { hex: '#fff5e8', sun: 2.8, amb: 0.5 },
  dusk: { hex: '#ff8c42', sun: 1.5, amb: 0.3 },
  night: { hex: '#3a5a8c', sun: 0.25, amb: 0.15 },
};
/** How much of the modelled sky diffuse the hemisphere keeps where the sky IBL carries it (a floor for IBL-less materials). */
export const HEMI_IBL_FRACTION = 0.12;
/** THE BAKED NIGHT's A/B switch (`__bakedNight(false)` = the old black night cube, the night's ambient all in the hemisphere). */
export const bakedNight = { on: true };
/** The night floor's tint (moonlight read by a light-adapted camera; the scotopic shift is P3's). */
const NIGHT_TINT = new THREE.Color(0.55, 0.66, 1.0);
/**
 * THE TOWN'S GLOW ON THE GROUND (the night-sky realism wave, 2026-10-06): the artificial sky's horizontal illuminance
 * (sky_sampler.nightSkyLive().artLux — ∫ L cos θ dΩ of the contract's artificial sky) joins the hemisphere as a warm term:
 * ~0.02–0.05 lux under a Bortle 8–9 sky (Jechow & Kyba 2022 measured 0.073 lux in Berlin's centre), ×3–10 under a deck
 * (0.79 lux overcast). ADDED to the ambient only — never to the global the exposure curve meters (the curve would
 * cancel it at the drop's cap) and never to the night floor; 0 in the wilds and by day (× the night factor: the lamps).
 */
const TOWN_GLOW_GAIN = 1;
/**
 * …and its DISPLAY is soft-capped at a full moon's: g / (1 + g / TOWN_GLOW_SOFT_MAX_LUX), so the hemisphere gains at most
 * 2 × the floor (~+1.6 stops — the lift the curve gives a full moon, NIGHT_DISPLAY_LIFT 1.5). Physically an overcast
 * city's ground reaches 0.79 lx (Berlin, beyond a full moon); unmetered over the game's lifted 0.05 lx floor that read
 * ×13 (+3.7 stops) under an overcast capital — the eye adapts to a lit night as it does to the moon. A clear city's
 * 0.02 lx stays nearly whole (+36 %).
 */
const TOWN_GLOW_SOFT_MAX_LUX = 2 * NIGHT_FLOOR_LUX;
/** Cloud base altitude (km) the clouds' sun colour is integrated from. */
const CLOUD_BASE_KM = 1.5;
/** Sun re-position gate (CSM re-render): the direction must move ≥ this angle (≈ the old 0.0005 tod step). */
const SHADOW_STEP_COS = Math.cos((0.18 * Math.PI) / 180);
// ── THE MOON's light (2026-09-27, sky/post P2 — realism-additions §1.5) ──
/** Half-width (m) of the moon's one shadow box around the player, its map size, and the box's snap grid (a re-render when the player crosses a cell). */
const MOON_SHADOW_HALF = 70;
const MOON_SHADOW_MAP = 2048;
const MOON_SHADOW_SNAP = 6;
/** The moon light sits this far from its target along the moon's direction (the shadow camera's near/far span it). */
const MOON_LIGHT_DIST = 400;

// three r186 #34213 (enginex render/backports/threeR186.ts): r185.1's cascades were placed AFTER they rendered and their
// matrices were a frame stale — the sun's shadows trailed the camera. Patched once, at module load; no-op at r186+.
backportCsmShadowNode(CSMShadowNode as unknown as { prototype: object });

export class LightingModule implements IModule {
  readonly name = 'Lighting';
  private app!: App;
  private scene!: THREE.Scene;
  private renderer!: THREE.WebGPURenderer;
  private readonly sun = new THREE.DirectionalLight(0xfff2d8, 2.0);
  /** THE MOON (2026-09-27): exists from boot at intensity 0 (the light-set identity law — never toggled by `visible`). */
  private readonly moon = new THREE.DirectionalLight(0xffffff, 0);
  private readonly _moonCentre = new THREE.Vector3(Infinity, 0, 0);
  private readonly _moonLastDir = new THREE.Vector3(0, -2, 0);
  private moonLastLit = false;
  private readonly ambient = new THREE.HemisphereLight(0xbfd4ff, 0x4a4636, 0.5);
  private readonly profiles = new Map<Band, Profile>();
  private csm: CSMShadowNode | null = null;
  private readonly _tint = new THREE.Color();
  private readonly _c = new THREE.Color();
  private readonly _g = new THREE.Color();
  private cascadeCount = 0;
  private subscribed = false;
  /** The physical model needs no DB rows: ready from init (the rows are a tint override). */
  private ready = true;
  /** The sun direction the CSM last rendered for (re-render gate). */
  private readonly lastShadowDir = new THREE.Vector3(0, -2, 0);
  private readonly _dir = new THREE.Vector3();

  // `assets` is kept for API compatibility with verify harnesses (g3-lighting,
  // g3-shadows, g5-tiers) that construct `new LightingModule(assets)`.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    this.scene = app.world.scene;
    this.renderer = app.renderer.native as THREE.WebGPURenderer;
    this.applyTonemap();
    this.sun.target.position.set(0, 0, 0);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.bias = -0.0005;
    this.sun.shadow.normalBias = 0.02;
    this.scene.add(this.sun, this.sun.target, this.ambient);
    // THE MOON: added with the sun (the light set is fixed from boot); its shadow map only on high / ultra.
    this.moon.name = 'moon-light';
    this.moon.target.name = 'moon-target';
    this.scene.add(this.moon, this.moon.target);
    this.configureMoonShadow();

    // CSM sun shadows (the WebGPU node CSM) — shadow cost BOUNDED by the per-shadow
    // autoUpdate gate: a static sun skips the cascade depth passes entirely.
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.sunShadowGate().autoUpdate = false;
    this.buildCsm();

    bus.on('atlas:account-ready', () => { this.subscribe(); void this.boot(); });
    bus.on('atlas:structures-updated', () => { this.requestShadowUpdate(); this.moonShadowGate().needsUpdate = true; });
    bus.on('atlas:quality-changed', () => { this.buildCsm(); this.applyTonemap(); this.configureMoonShadow(); });
    for (const ev of ['atlas:environment-updated', 'atlas:world-entered'] as const) bus.on(ev, () => this.applyFromSky());

    // Continuous drive (RenderSub/4, ahead of the sky uniforms at 5, the engine
    // bake at 6 and the clouds/weather at 22): colour, intensity and exposure
    // every frame (uniform-cheap); the sun's POSITION — which invalidates every
    // CSM cascade — steps behind an angle gate.
    app.world.addSystem(makeSystem('AtlasDayNight', Phase.RenderSub, 4, () => {
      this.drainCsmDisposals();
      if (this.ready) this.applyTimeOfDay(currentTod());
    }, Sub.Update));
    // THE CAVE HOOK on the sky IBL: after SkyModule's carrier writes environmentIntensity (RenderSub/7) — one multiply, never compounding.
    app.world.addSystem(makeSystem('AtlasCaveHook', Phase.RenderSub, 8, (dt) => applyCaveHookToEnv(this.scene as unknown as { environmentIntensity: number }, dt), Sub.Update));

    const g = globalThis as Record<string, unknown>;
    g.__lighting = () => this.stats();
    g.__bakedNight = (on?: boolean) => { if (typeof on === 'boolean') bakedNight.on = on; return { on: bakedNight.on, nightIblLux: lightFrame.nightIblLux, live: lightFrame.nightIblLive, applied: lightFrame.nightIblApplied }; };
    g.__exposure = () => ({ ...exposureState, emissiveScaleAt0: emissiveEvScale(0), emissiveScaleAt5: emissiveEvScale(5), keyNits: keyLuminanceNits(), flameScene: emissiveNits(EMISSIVE_NITS.flame), keyScene: { ...lightFrame.keyScene }, skyScene: { ...lightFrame.skyScene } });
    g.__sunSeason = (doy: number | null, lat: number | null = null) => { pinSunSeason(doy, lat); this.lastShadowDir.set(0, -2, 0); return sunObserver(); };
    // Probe pin without the inspection-light rig (which adds a flat ambient and nulls the fog): the weather at the player.
    g.__lightingWeather = (kind: string | null, snap = true) => {
      const wm = this.app.getModule('Weather') as { setDebugWeather?: (w: string | null, snap?: boolean) => void; setDebugSnow?: (v: number | null) => void } | undefined;
      wm?.setDebugWeather?.(kind, snap);
      // A SNAPPED pin snaps the ground too (session 2): snow accumulation melts at 2 %/s of FRAME time, so after a boot
      // under the server's snow a pinned clear noon stood on white ground for the whole SwiftShader session.
      if (snap) wm?.setDebugSnow?.(kind === null ? null : kind === 'snow' ? 1 : 0);
      return kind;
    };
    // Probe inspection (session 2): the handles a diagnostic capture varies right before `__frameCapture` renders
    // (the fog density, the renderer's exposure) — the probe runs without the `atlas:probe` rig, so `__app` is absent.
    g.__lightingDiag = () => ({ scene: this.scene, renderer: this.renderer, sun: this.sun, hemi: this.ambient, moon: this.moon });
    // THE MOON's levers (2026-09-27): the state this frame, and a phase pin (0 new, 0.5 full; null releases).
    g.__moon = () => this.moonStats();
    g.__moonPhase = (p: number | null) => { pinMoonPhase(p); this._moonLastDir.set(0, -2, 0); return moonPhasePinned(); };
    // Probe diagnostic: the moon's shadow map on / off (a light-set change — every lit material rebuilds once).
    g.__cloudMoonKey = (on?: boolean) => { if (typeof on === 'boolean') cloudKey.moonEnabled = on; return { ...cloudKey, dir: { ...cloudKey.dir } }; }; // sky/post (2026-09-27): the clouds' moon key A/B
    g.__moonShadows = (on: boolean) => { (activePreset() as { moonShadows?: boolean }).moonShadows = !!on; this.configureMoonShadow(); return this.moon.castShadow; };
    // Probe planning (pure — no frame): where the moon stands at `tod` for an observer at `lat` (default the live one) on the live season.
    g.__moonAt = (tod: number, lat: number | null = null) => { const o = sunObserver(); return moonAt(Date.now() / 1000 / Math.max(60, this.cycleSeconds()), tod, lat ?? o.latDeg, o.dayOfYear, MOON_SYNODIC_DAYS); };
  }

  /** CSM nodes waiting for deferred disposal (Dawn destroyed-texture guard: two frames of grace). */
  private readonly pendingCsmDispose: Array<{ node: { dispose?: () => void }; frames: number }> = [];
  private deferCsmDispose(node: unknown): void { this.pendingCsmDispose.push({ node: node as { dispose?: () => void }, frames: 2 }); }
  private drainCsmDisposals(): void {
    for (let i = this.pendingCsmDispose.length - 1; i >= 0; i--) {
      const p = this.pendingCsmDispose[i];
      if (--p.frames <= 0) {
        try { p.node.dispose?.(); } catch { /* already gone */ }
        this.pendingCsmDispose.splice(i, 1);
      }
    }
  }

  /**
   * The operator (AgX by default — the hue-stable path to white, §10 R2; ACES
   * 1.x / Neutral / Reinhard stay selectable; 'aces2' is the engine's
   * hue-preserving node applied at the pipeline's display boundary, the
   * renderer keeps AgX for any path outside the pipeline) and the exposure
   * mode. The exposure VALUE is written every frame by applyTimeOfDay.
   */
  private applyTonemap(): void {
    const p = activePreset();
    const ops: Record<string, THREE.ToneMapping> = {
      aces: THREE.ACESFilmicToneMapping, agx: THREE.AgXToneMapping, aces2: THREE.AgXToneMapping,
      neutral: THREE.NeutralToneMapping, reinhard: THREE.ReinhardToneMapping,
    };
    this.renderer.toneMapping = ops[p.tonemapOperator ?? 'agx'] ?? THREE.AgXToneMapping;
    exposureState.mode = p.exposureMode === 'fixed' ? 'fixed' : 'physical';
    this.writeExposure();
  }

  /** The one ground writer of toneMappingExposure (space: the sector's own eye owns it). */
  private writeExposure(): void {
    if (atlas.spaceFlight) return;
    const p = activePreset();
    if (exposureState.mode === 'fixed') {
      exposureState.compensation = 1;
      exposureState.exposure = p.tonemapExposure ?? EXPOSURE_REF;
    } else {
      exposureState.compensation = (p.tonemapExposure ?? EXPOSURE_REF) / EXPOSURE_REF;
      // In a headset a night too dark for the display gets stops back (photometry.xrNightLiftEv — the Quest 3 black screen).
      const lift = this.renderer.xr?.isPresenting ? xrNightLiftEv(exposureState.ev100 - exposureState.evScene) : 0;
      exposureState.xrLiftEv = lift;
      exposureState.exposure = EXPOSURE_REF * exposureState.compensation * Math.pow(2, exposureState.correctionEv + lift);
    }
    exposureState.exposureRef = EXPOSURE_REF;
    this.renderer.toneMappingExposure = exposureState.exposure;
  }

  private buildCsm(): void {
    const preset = activePreset();
    if (!preset.shadowsEnabled) {
      const prev = this.csm;
      this.csm = null;
      this.cascadeCount = 0;
      this.sun.castShadow = false;
      (this.sun.shadow as unknown as { shadowNode: CSMShadowNode | null }).shadowNode = null;
      if (prev) this.deferCsmDispose(prev);
      return;
    }
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(preset.shadowMapSize, preset.shadowMapSize);
    // NORMAL BIAS SCALED TO THE TEXEL (2026-09-15): normalBias offsets the lookup
    // along the normal, so it must be about one shadow texel in world units —
    // cascade 0 of the 'practical' split ≈ maxFar/(4·mapSize); ~1.5 of them,
    // clamped. (The law holds for the lower, longer-shadowed sun of 2026-09-25.)
    const texelM = preset.shadowMaxFar / (4 * Math.max(64, preset.shadowMapSize));
    this.sun.shadow.normalBias = Math.min(0.25, Math.max(0.015, texelM * 1.5));
    const coarse = typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;
    const cascades = coarse ? Math.min(2, preset.shadowCascades) : preset.shadowCascades;
    const prev = this.csm;
    try {
      const csm = new CSMShadowNode(this.sun, { cascades, maxFar: preset.shadowMaxFar, mode: 'practical', lightMargin: 100 });
      csm.fade = true;
      (this.sun.shadow as unknown as { shadowNode: CSMShadowNode }).shadowNode = csm;
      this.csm = csm;
      this.cascadeCount = cascades;
      if (prev) this.deferCsmDispose(prev);
      this.requestShadowUpdate();
    } catch (e) { console.warn('[g3] CSMShadowNode unavailable — single shadow map', e); }
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => void this.boot())
      .subscribe(['SELECT * FROM sky_lighting_profile', 'SELECT * FROM render_asset', 'SELECT * FROM world', 'SELECT * FROM sky_state']);
  }

  /** Load the band profiles (a tint override now), then apply the current time-of-day. */
  private async boot(): Promise<void> {
    const conn = atlas.conn;
    if (!conn) return;
    for (const r of conn.db.skyLightingProfile.iter()) {
      this.profiles.set(r.band as Band, { envmapAssetId: r.envmapAssetId, sunColorHex: r.sunColorHex, sunIntensity: r.sunIntensity, ambientIntensity: r.ambientIntensity });
    }
    this.applyFromSky();
  }

  private applyFromSky(): void {
    if (this.ready) this.applyTimeOfDay(currentTod());
  }

  /** The current world's sky cycle length (the season's clock), default 2 h. */
  private cycleSeconds(): number {
    try {
      const conn = atlas.conn; const wid = atlas.currentWorldId ?? atlas.commonsWorldId;
      const s = conn && wid != null ? conn.db.skyState.worldId.find(wid) : undefined;
      return s?.cycleSeconds || 7200;
    } catch { return 7200; }
  }

  /** The weather the light sees (the Weather module's resolved, lerped field). */
  private weatherInputs(): { cover: number; density: number; sunDim: number; precip: number } {
    const w = this.app.getModule('Weather') as { getLightWeather?: () => { cover: number; density: number; sunDim: number; precip: number } } | undefined;
    const v = w?.getLightWeather?.();
    return v && atlas.currentWorldId !== null && atlas.currentWorldId === atlas.commonsWorldId ? v : { cover: 0, density: 1, sunDim: 0, precip: 0 };
  }

  /** The band profile as a (tint, sunMul, ambMul) override — neutral when the row is the seed. */
  private profileOverride(tod: number): { tint: string | null; tintW: number; sunMul: number; ambMul: number } {
    const { band, next, blend } = bandForTimeOfDay(tod);
    const a = this.profiles.get(band), b = this.profiles.get(next);
    if (!a || !b) return { tint: null, tintW: 0, sunMul: 1, ambMul: 1 };
    const mul = (p: Profile, s: { sun: number; amb: number }) => ({ sun: s.sun > 0 ? p.sunIntensity / s.sun : 1, amb: s.amb > 0 ? p.ambientIntensity / s.amb : 1 });
    const ma = mul(a, SEED_PROFILE[band]), mb = mul(b, SEED_PROFILE[next]);
    const custom = (p: Profile, s: { hex: string }) => p.sunColorHex.toLowerCase() !== s.hex;
    const useA = custom(a, SEED_PROFILE[band]), useB = custom(b, SEED_PROFILE[next]);
    const tint = blend < 0.5 ? (useA ? a.sunColorHex : null) : (useB ? b.sunColorHex : null);
    return { tint, tintW: tint ? 0.25 : 0, sunMul: THREE.MathUtils.lerp(ma.sun, mb.sun, blend), ambMul: THREE.MathUtils.lerp(ma.amb, mb.amb, blend) };
  }

  /** Drive the sun path, the key colour/intensity, the hemisphere and the exposure for a time-of-day. */
  applyTimeOfDay(tod: number): void {
    setSunObserver(latitudeForZ(atlas.localZ), worldDayOfYear(Date.now() / 1000, this.cycleSeconds()));
    const d = sunDirection(tod);
    const sampler = skySampler();
    const sky = sampler.sample(d.y);
    const tAtm = sampler.transmittance(d.y);
    const w = this.weatherInputs();
    // THE MOON (2026-09-27): reflected sunlight through the same air and deck; its global takes the night floor down
    // toward the moonless starlight (photometry `nightFloorLux`) and joins the scene's global (the curve's evScene).
    const obs = sunObserver();
    const mo = moonAt(Date.now() / 1000 / Math.max(60, this.cycleSeconds()), tod, obs.latDeg, obs.dayOfYear, MOON_SYNODIC_DAYS);
    const cl = cloudinessOf(w.cover, w.density);
    const mm = moonModel({ sinEl: mo.dir.y, tAtm: sampler.transmittance(mo.dir.y), tAtmZenith: sampler.transmittance(1), skyIrr: sampler.sample(mo.dir.y).irradiance, alphaDeg: mo.alphaDeg, cloudiness: cl, tCloud: tCloudOf(cl), sunDim: w.sunDim });
    const floorLux = nightFloorLux(mm.globalLux);
    const m = lightModel({ sinEl: d.y, tAtm, skyIrr: sky.irradiance, cover: w.cover, density: w.density, sunDim: w.sunDim, nightLux: floorLux + mm.globalLux });
    // Fixed exposure (a menu option): the lights keep noon's pre-exposure — a real fixed camera (nights go dark).
    const k = exposureState.mode === 'fixed' ? luxToSceneFor(EV_DAY) : m.luxToScene;
    exposureState.ev100 = m.ev100; exposureState.evScene = m.evScene; exposureState.luxToScene = k;

    const boost = activePreset().lightingBoost;
    const env = worldEnv();
    const prof = this.profileOverride(tod);

    // ── The key ──
    this.sun.color.setRGB(m.keyColor[0], m.keyColor[1], m.keyColor[2]);
    if (prof.tint) this.sun.color.lerp(this._tint.set(prof.tint), prof.tintW);
    if (env.sunTintHex) this.sun.color.lerp(this._tint.set(env.sunTintHex), TINT_BLEND);
    const colLum = Math.max(1e-6, lum709(this.sun.color.r, this.sun.color.g, this.sun.color.b));
    const keyLum = lum709(m.keyColor[0], m.keyColor[1], m.keyColor[2]);
    this.sun.intensity = (m.keyLux * keyLum / colLum) * k * boost * env.sunMul * prof.sunMul;

    // ── The ambient: sky-tinted; a floor where the sky IBL carries the diffuse ──
    const hasIbl = !!(this.scene as { environment?: unknown }).environment;
    lightFrame.ibl = hasIbl;
    const frac = hasIbl ? HEMI_IBL_FRACTION : 1;
    const skyLux = m.diffuseLux * frac;
    this._c.setRGB(m.diffuseColor[0], m.diffuseColor[1], m.diffuseColor[2]).multiplyScalar(skyLux).add(this._g.copy(NIGHT_TINT).multiplyScalar(floorLux));
    this._c.add(this._g.setRGB(mm.diffuseColor[0], mm.diffuseColor[1], mm.diffuseColor[2]).multiplyScalar(mm.diffuseLux)); // the moonlit sky (not in the IBL cube)
    const glowLux = this.townGlow(m.cloudiness); // 2026-10-06: the town's glow
    if (glowLux > 0) { const t = this.glowTint, tm = Math.max(1e-6, t[0], t[1], t[2]); this._c.add(this._g.setRGB(t[0] / tm, t[1] / tm, t[2] / tm).multiplyScalar(glowLux)); }
    // THE BAKED NIGHT (2026-10-06): where the sky IBL exists, the cube carries (1 − HEMI_IBL_FRACTION) of the night's
    // ambient (the floor + the town's glow) × the night factor — SkyModule bakes it shaped as the drawn night sky, so a
    // shiny surface reflects the night (the cube was black) — and the hemisphere keeps the rest, as by day. The moonlit
    // sky stays in the hemisphere. 0 by day, without an IBL, or with the A/B off (`__bakedNight(false)`).
    const nfB = Math.max(0, Math.min(1, (uNightFactor as unknown as { value: number }).value));
    const q = hasIbl && bakedNight.on ? (1 - HEMI_IBL_FRACTION) * nfB : 0; // the share of the night's ambient the cube carries
    lightFrame.nightIblLux = q * (floorLux + glowLux); // what the NEXT bake puts in the cube (SkyModule reads it)
    // THE LIVE NIGHT (2026-10-06): the hemisphere gives up what the LIVE cube carries (SkyModule's lightFrame.nightIblLive),
    // never what the next bake will — cut at once, the scene metered 10× darker until the night cube landed (lib/sky/night_ibl).
    const nightAmb = floorLux + glowLux;
    const nightIbl = hasIbl ? nightIblApplied(lightFrame.nightIblLive, (1 - HEMI_IBL_FRACTION) * nightAmb) : 0;
    lightFrame.nightIblApplied = nightIbl;
    if (nightIbl > 0) { // the same channel recipe as above, × the moved fraction: NIGHT_TINT × floor + the glow's max-normalised tint × glow
      const f = nightIbl / Math.max(1e-9, nightAmb);
      const t = this.glowTint, tm = Math.max(1e-6, t[0], t[1], t[2]);
      this._c.r = Math.max(0, this._c.r - f * (NIGHT_TINT.r * floorLux + (t[0] / tm) * glowLux));
      this._c.g = Math.max(0, this._c.g - f * (NIGHT_TINT.g * floorLux + (t[1] / tm) * glowLux));
      this._c.b = Math.max(0, this._c.b - f * (NIGHT_TINT.b * floorLux + (t[2] / tm) * glowLux));
    }
    const hemiLux = skyLux + floorLux + mm.diffuseLux + glowLux - nightIbl;
    const cMax = Math.max(1e-9, this._c.r, this._c.g, this._c.b);
    this.ambient.color.setRGB(this._c.r / cMax, this._c.g / cMax, this._c.b / cMax);
    const cLum = Math.max(1e-6, lum709(this.ambient.color.r, this.ambient.color.g, this.ambient.color.b));
    this.ambient.intensity = (hemiLux / cLum) * k * boost * env.ambientMul * prof.ambMul;
    // THE HEMISPHERE'S UP (2026-10-06): three r185's HemisphereLightNode takes "up" as normalize(the light's WORLD
    // position), and the FP64 rebase holds scene.position at −origin — past 10 km from the world origin the sky colour
    // came from the horizon toward the origin (an up-facing surface took half sky, half ground). Seat it at world (0, 1, 0).
    { const sp = this.scene.position; this.ambient.position.set(-sp.x, 1 - sp.y, -sp.z); }
    // Ground bounce: the ground's albedo under what reaches it (warm key + the sky), as a hue at the same level.
    const keyH = m.keyLux * keyLum * Math.max(0, d.y) + mm.keyLumLux * Math.max(0, mo.dir.y);
    this._g.setRGB(m.keyColor[0] * keyH + m.diffuseColor[0] * m.diffuseLux, m.keyColor[1] * keyH + m.diffuseColor[1] * m.diffuseLux, m.keyColor[2] * keyH + m.diffuseColor[2] * m.diffuseLux);
    const gMax = Math.max(1e-9, this._g.r, this._g.g, this._g.b);
    const bounceRatio = Math.min(1, (GROUND_ALBEDO * (keyH + m.diffuseLux)) / Math.max(1e-6, hemiLux * Math.PI));
    this.ambient.groundColor.setRGB((this._g.r / gMax) * bounceRatio * 0.8 + 0.02, (this._g.g / gMax) * bounceRatio * 0.72 + 0.02, (this._g.b / gMax) * bounceRatio * 0.6 + 0.02);
    // THE CAVE HOOK (shader gaps 2026-09-29): inside a cave the hemisphere takes the baked sky transfer at the player (× m) +
    // the crystals' light (+ t) — eased, identity outside every cave. lib/cave_light_hook.ts; the cave's own rock undoes it.
    applyCaveHookToHemisphere(this.ambient);

    // ── The light frame everyone reads ──
    lightFrame.valid = true; lightFrame.model = m; lightFrame.sinEl = d.y;
    // The physical-emissive contract (2026-09-26): what the key and the whole sky ARE in scene units, for shaders that
    // light themselves (water, impostors, cards) — the DirectionalLight exactly, and the sky before the IBL's floor cut.
    { const ks = this.sun.intensity; lightFrame.keyScene.x = this.sun.color.r * ks; lightFrame.keyScene.y = this.sun.color.g * ks; lightFrame.keyScene.z = this.sun.color.b * ks; }
    { // lux are LUMINANCE-weighted: a max-normalised colour c carries lux/lum(c) per channel (the hemisphere's own rule)
      const sk = k * boost * env.ambientMul * prof.ambMul;
      const dl = m.diffuseLux / Math.max(1e-6, lum709(m.diffuseColor[0], m.diffuseColor[1], m.diffuseColor[2]));
      const nl = floorLux / Math.max(1e-6, lum709(NIGHT_TINT.r, NIGHT_TINT.g, NIGHT_TINT.b));
      const ml = mm.diffuseLux / Math.max(1e-6, lum709(mm.diffuseColor[0], mm.diffuseColor[1], mm.diffuseColor[2]));
      const gt = this.glowTint; // luminance 1: lux per channel (the rule above)
      lightFrame.skyScene.x = (m.diffuseColor[0] * dl + NIGHT_TINT.r * nl + mm.diffuseColor[0] * ml + gt[0] * glowLux) * sk;
      lightFrame.skyScene.y = (m.diffuseColor[1] * dl + NIGHT_TINT.g * nl + mm.diffuseColor[1] * ml + gt[1] * glowLux) * sk;
      lightFrame.skyScene.z = (m.diffuseColor[2] * dl + NIGHT_TINT.b * nl + mm.diffuseColor[2] * ml + gt[2] * glowLux) * sk; }
    lightFrame.nightLux = floorLux + mm.globalLux;
    lightFrame.sunDir.x = d.x; lightFrame.sunDir.y = d.y; lightFrame.sunDir.z = d.z;
    this.driveMoon(mo, mm, k * boost * env.sunMul);
    lightFrame.skyMean = sky.meanRadiance; lightFrame.zenith = sky.zenith; lightFrame.horizon = sky.horizon; lightFrame.horizonSun = sky.horizonSun;
    lightFrame.cloudSunT = sampler.transmittance(d.y, CLOUD_BASE_KM);
    lightFrame.cloudSunTHigh = sampler.transmittance(d.y, CLOUD_HIGH_KM);
    lightFrame.cover = w.cover; lightFrame.density = w.density; lightFrame.sunDim = w.sunDim; lightFrame.precip = w.precip;

    this.writeExposure();
    this.driveEmissives();

    // ── The sun's POSITION (CSM re-render): only while the key is up, only when it moved ≥ 0.18° ──
    if (m.fade <= 0) return;
    this._dir.set(d.x, d.y, d.z);
    if (this._dir.dot(this.lastShadowDir) >= SHADOW_STEP_COS) return;
    this.lastShadowDir.copy(this._dir);
    this.sun.position.copy(sunPosition(tod));
    this.requestShadowUpdate();
  }

  /** The town's glow tint (luminance 1) and its last illuminance — `townGlow` writes both. */
  private readonly glowTint: [number, number, number] = [1.32, 0.94, 0.55];
  private glowLux = 0;
  /** THE TOWN'S GLOW ON THE GROUND (lux) this frame: the artificial sky's horizontal illuminance × the night factor; 0 off the Commons. */
  private townGlow(cloudiness: number): number {
    const sg = skyglowLive();
    const nf = (uNightFactor as unknown as { value: number }).value;
    if (!nightAir.glow || !sg.active || atlas.spaceFlight || !(nf > 0)) { this.glowLux = 0; return 0; }
    const ns = nightSkyLive(cloudiness);
    this.glowTint[0] = ns.artTint[0]; this.glowTint[1] = ns.artTint[1]; this.glowTint[2] = ns.artTint[2];
    const raw = ns.artLux * nf * TOWN_GLOW_GAIN;
    const v = raw / (1 + raw / TOWN_GLOW_SOFT_MAX_LUX);
    this.glowLux = Number.isFinite(v) && v > 0 ? v : 0;
    return this.glowLux;
  }

  /** R9: lit interior rooms and the window area lights on the EV scale (emissives read the same under the metering). */
  private emissiveFrame = 0;
  private driveEmissives(): void {
    if ((++this.emissiveFrame & 7) !== 0 || !interiorHandles.size) return;
    const v = emissiveEvScale(INTERIOR_EV_REL);
    for (const h of interiorHandles) { const li = (h as { lightIntensity?: { value: number } }).lightIntensity; if (li) li.value = v; }
  }

  // ── THE MOON (2026-09-27, sky/post P2 — realism-additions §1.5) ──
  private moonShadowGate(): { autoUpdate: boolean; needsUpdate: boolean } {
    return this.moon.shadow as unknown as { autoUpdate: boolean; needsUpdate: boolean };
  }
  /** The moon's shadow per tier (`moonShadows`: high / ultra): ONE map in a box around the player, re-rendered only on a gate. */
  private configureMoonShadow(): void {
    const p = activePreset();
    const want = !!p.moonShadows && p.shadowsEnabled;
    if (this.moon.castShadow !== want) this.moon.castShadow = want;
    if (!want) return;
    const sh = this.moon.shadow;
    sh.mapSize.set(MOON_SHADOW_MAP, MOON_SHADOW_MAP);
    const cam = sh.camera as THREE.OrthographicCamera;
    cam.left = -MOON_SHADOW_HALF; cam.right = MOON_SHADOW_HALF; cam.top = MOON_SHADOW_HALF; cam.bottom = -MOON_SHADOW_HALF;
    cam.near = 1; cam.far = MOON_LIGHT_DIST * 2;
    cam.updateProjectionMatrix();
    sh.bias = -0.0005;
    sh.normalBias = Math.min(0.25, Math.max(0.02, ((2 * MOON_SHADOW_HALF) / MOON_SHADOW_MAP) * 1.5)); // ~1.5 texels (the sun's rule)
    this.moonShadowGate().autoUpdate = false;
    this.moonShadowGate().needsUpdate = true;
  }
  private readonly _moonKey = { x: 0, y: 0, z: 0 };
  /**
   * The moon light this frame: colour/intensity from the model (lux × the curve × the tier boost × the world's sun
   * multiplier — it IS sunlight), direction from the schedule, its shadow box snapped around the player (ABSOLUTE
   * coordinates, like every scene child), and the frame's KEY = the brighter of the sun and the moon.
   */
  private driveMoon(mo: { dir: { x: number; y: number; z: number }; alphaDeg: number; phase: number }, mm: MoonModel, scale: number): void {
    const I = atlas.spaceFlight ? 0 : mm.keyLux * scale;
    this.moon.color.setRGB(mm.keyColor[0], mm.keyColor[1], mm.keyColor[2]);
    this.moon.intensity = I;
    const mk = this._moonKey; mk.x = mm.keyColor[0] * I; mk.y = mm.keyColor[1] * I; mk.z = mm.keyColor[2] * I;
    const snap = (v: number): number => Math.round((Number.isFinite(v) ? v : 0) / MOON_SHADOW_SNAP) * MOON_SHADOW_SNAP;
    const cx = snap(atlas.localX), cy = snap(atlas.localY), cz = snap(atlas.localZ);
    this.moon.target.position.set(cx, cy, cz);
    this.moon.position.set(cx + mo.dir.x * MOON_LIGHT_DIST, cy + mo.dir.y * MOON_LIGHT_DIST, cz + mo.dir.z * MOON_LIGHT_DIST);
    // The shadow re-renders only while the moon lights the ground, and only when the box moved a cell, the moon
    // moved ≥ 0.18° (the sun's gate), or it just rose / the phase pin changed (casters: atlas:structures-updated).
    const lit = this.moon.castShadow && I > 1e-9 && mo.dir.y > -0.02;
    if (lit) {
      this._dir.set(mo.dir.x, mo.dir.y, mo.dir.z);
      const moved = this._moonCentre.x !== cx || this._moonCentre.y !== cy || this._moonCentre.z !== cz || this._dir.dot(this._moonLastDir) < SHADOW_STEP_COS;
      if (moved || !this.moonLastLit) { this._moonCentre.set(cx, cy, cz); this._moonLastDir.copy(this._dir); this.moonShadowGate().needsUpdate = true; }
    }
    this.moonLastLit = lit;
    const prev = lightFrame.moon;
    lightFrame.moon = {
      dir: prev?.dir ?? { x: 0, y: 0, z: 0 }, sinEl: mo.dir.y, alphaDeg: mo.alphaDeg, phase: mo.phase, illum: moonIllumFraction(mo.alphaDeg),
      model: mm, keyScene: prev?.keyScene ?? { x: 0, y: 0, z: 0 }, floorLux: lightFrame.nightLux - mm.globalLux,
    };
    const f = lightFrame.moon;
    f.dir.x = mo.dir.x; f.dir.y = mo.dir.y; f.dir.z = mo.dir.z;
    f.keyScene.x = mk.x; f.keyScene.y = mk.y; f.keyScene.z = mk.z;
    // THE KEY: whichever lights the ground more (the water's road, the soot's lobe follow it).
    const sunKey = lightFrame.keyScene, sd = lightFrame.sunDir;
    const moonWins = lum709(mk.x, mk.y, mk.z) > lum709(sunKey.x, sunKey.y, sunKey.z);
    lightFrame.keyIsMoon = moonWins;
    if (moonWins) { sunKey.x = mk.x; sunKey.y = mk.y; sunKey.z = mk.z; lightFrame.keyDir.x = mo.dir.x; lightFrame.keyDir.y = mo.dir.y; lightFrame.keyDir.z = mo.dir.z; }
    else { lightFrame.keyDir.x = sd.x; lightFrame.keyDir.y = sd.y; lightFrame.keyDir.z = sd.z; }
  }
  private moonStats(): Record<string, unknown> | null {
    const f = lightFrame.moon;
    if (!f) return null;
    const r = (v: number, n = 4): number => +v.toFixed(n);
    return {
      dir: [r(f.dir.x), r(f.dir.y), r(f.dir.z)], elevationDeg: r((Math.asin(Math.max(-1, Math.min(1, f.sinEl))) * 180) / Math.PI, 2),
      phase: r(f.phase), alphaDeg: r(f.alphaDeg, 1), illumFraction: r(f.illum, 3), phaseLaw: r(f.model.phaseLaw), pinned: moonPhasePinned(),
      e0Lux: r(f.model.e0Lux), keyLux: r(f.model.keyLux, 5), keyLumLux: r(f.model.keyLumLux, 5), diffuseLux: r(f.model.diffuseLux, 5),
      globalLux: r(f.model.globalLux, 5), floorLux: r(f.floorLux, 5), nightLux: r(lightFrame.nightLux, 5),
      intensity: r(this.moon.intensity, 6), color: `#${this.moon.color.getHexString()}`, castShadow: this.moon.castShadow,
      keyIsMoon: lightFrame.keyIsMoon, keyScene: [r(f.keyScene.x, 6), r(f.keyScene.y, 6), r(f.keyScene.z, 6)],
    };
  }

  private sunShadowGate(): { autoUpdate: boolean; needsUpdate: boolean } {
    return this.sun.shadow as unknown as { autoUpdate: boolean; needsUpdate: boolean };
  }

  /** Re-render the CSM shadows ONCE (sun moved / casters changed), keeping the cascades gated. */
  requestShadowUpdate(): void {
    this.sunShadowGate().needsUpdate = true;
    if (this.csm) for (const lw of this.csm.lights) { lw.shadow.autoUpdate = false; lw.shadow.needsUpdate = true; }
  }

  /** Global IBL strength (environmentIntensity) — the g3-lighting harness lever. */
  setEnvIntensity(v: number): void { this.scene.environmentIntensity = v; }

  private stats(): Record<string, unknown> {
    const m = lightFrame.model;
    const obs = sunObserver();
    const d = sunDirection(currentTod());
    return {
      tod: +currentTod().toFixed(4), observer: { ...obs, noonElevationDeg: +noonElevationDeg(obs.latDeg, obs.declDeg).toFixed(2) },
      sunDir: [+d.x.toFixed(4), +d.y.toFixed(4), +d.z.toFixed(4)], elevationDeg: +(Math.asin(Math.max(-1, Math.min(1, d.y))) * 180 / Math.PI).toFixed(2),
      key: { intensity: +this.sun.intensity.toFixed(4), color: `#${this.sun.color.getHexString()}`, lux: m ? Math.round(m.keyLux) : null, fade: m?.fade ?? null },
      hemi: { intensity: +this.ambient.intensity.toFixed(4), sky: `#${this.ambient.color.getHexString()}`, ground: `#${this.ambient.groundColor.getHexString()}`, ibl: lightFrame.ibl },
      model: m ? { diffuseLux: Math.round(m.diffuseLux), globalLux: +m.globalLux.toFixed(3), evScene: +m.evScene.toFixed(2), ev100: +m.ev100.toFixed(2), drop: +m.drop.toFixed(2), cloudiness: +m.cloudiness.toFixed(3), tCloud: +m.tCloud.toFixed(3), luxToScene: m.luxToScene } : null,
      weather: { cover: lightFrame.cover, density: lightFrame.density, sunDim: lightFrame.sunDim, precip: lightFrame.precip },
      exposure: { ...exposureState }, toneMapping: this.renderer.toneMapping, envIntensity: this.scene.environmentIntensity,
      e0Scene: E0_LUX * exposureState.luxToScene, shadows: { cascades: this.cascadeCount, map: this.shadowMapSize, far: this.shadowMaxFar },
      moon: this.moonStats(),
      townGlow: { lux: +this.glowLux.toFixed(5), tint: this.glowTint.map((v) => +v.toFixed(3)) },
    };
  }

  /** Test/inspection hooks. */
  get sunLight(): THREE.DirectionalLight { return this.sun; }
  get ambientLight(): THREE.HemisphereLight { return this.ambient; }
  get isReady(): boolean { return this.ready; }
  get shadowCascades(): number { return this.cascadeCount; }
  get shadowMapSize(): number { return this.sun.shadow.mapSize.x; }
  get shadowMaxFar(): number { return (this.csm as unknown as { maxFar?: number } | null)?.maxFar ?? 0; }
  get hasCSM(): boolean { return this.csm !== null; }

  dispose(): void {
    this.drainCsmDisposals();
    try { this.csm?.dispose(); } catch { /* Dawn destroyed-texture guard */ }
    this.csm = null;
    this.sun.parent?.remove(this.sun);
    this.moon.parent?.remove(this.moon); this.moon.target.parent?.remove(this.moon.target);
    this.ambient.parent?.remove(this.ambient);
  }
}

/** Lit rooms behind the panes: +1 EV over the reference (the screen class of §10 R9 — a lit room, not a neon sign). */
const INTERIOR_EV_REL = 1;
